import { useEffect, useMemo, useState } from "react";
import { Check, Clock, Coins, ArrowUpDown, Filter, Sparkles } from "lucide-react";

// Regex parses each prompt title in Track 38, e.g.
//   "01 · AI Prompt Packs — 1-2 days · £9-£29"
// into { order, name, build_low, build_high, price_low, price_high }.
const TITLE_RE = /^(\d+)\s*·\s*(.+?)\s+—\s+(\d+)(?:-(\d+))?\s*days?\s*·\s*£(\d+)-£(\d+)/i;

function parsePrompt(prompt, idx) {
  const m = TITLE_RE.exec(prompt.title || "");
  if (!m) {
    return {
      idx,
      order: idx + 1,
      name: prompt.title || `Prompt ${idx + 1}`,
      buildLow: null,
      buildHigh: null,
      priceLow: null,
      priceHigh: null,
      raw: prompt.title,
    };
  }
  const [, order, name, bLow, bHigh, pLow, pHigh] = m;
  return {
    idx,
    order: Number(order),
    name: name.trim(),
    buildLow: Number(bLow),
    buildHigh: Number(bHigh || bLow),
    priceLow: Number(pLow),
    priceHigh: Number(pHigh),
    raw: prompt.title,
  };
}

const PRICE_BANDS = [
  { key: "all", label: "All prices", min: 0, max: Infinity },
  { key: "starter", label: "£7–£29 · Quick wins", min: 0, max: 29 },
  { key: "mid", label: "£30–£99 · Mid-tier", min: 30, max: 99 },
  { key: "premium", label: "£100+ · Flagship", min: 100, max: Infinity },
];

const SORTS = [
  { key: "default", label: "Default order" },
  { key: "build-asc", label: "Fastest to build" },
  { key: "price-asc", label: "Lowest price first" },
  { key: "price-desc", label: "Highest price first" },
];

const LS_KEY = "stagvault_dd_built_v1";

function loadBuilt() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}
function saveBuilt(set) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...set]));
  } catch {
    /* no-op */
  }
}

function ProgressRing({ pct, size = 92, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - pct);
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="dd-progress-ring">
      <defs>
        <linearGradient id="ddGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#b070ff" />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="url(#ddGrad)"
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 500ms ease" }}
      />
      <text
        x="50%"
        y="50%"
        dy="0.35em"
        textAnchor="middle"
        className="font-display font-black fill-zinc-100"
        fontSize={size * 0.28}
      >
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
}

export function DigitalDownloadsPicker({ prompts, onJump }) {
  const parsed = useMemo(() => prompts.map(parsePrompt), [prompts]);
  const [built, setBuilt] = useState(() => loadBuilt());
  const [sort, setSort] = useState("default");
  const [band, setBand] = useState("all");
  const [showBuilt, setShowBuilt] = useState(true);

  useEffect(() => {
    saveBuilt(built);
  }, [built]);

  function toggle(idx) {
    setBuilt((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  const bandCfg = PRICE_BANDS.find((b) => b.key === band) || PRICE_BANDS[0];
  const filtered = parsed
    .filter((p) => {
      if (p.priceLow == null) return true; // parse-fail: keep visible
      const inBand = p.priceLow >= bandCfg.min && p.priceLow <= bandCfg.max;
      const builtOk = showBuilt || !built.has(p.idx);
      return inBand && builtOk;
    })
    .sort((a, b) => {
      if (sort === "build-asc") return (a.buildHigh ?? 999) - (b.buildHigh ?? 999) || a.order - b.order;
      if (sort === "price-asc") return (a.priceLow ?? 9999) - (b.priceLow ?? 9999) || a.order - b.order;
      if (sort === "price-desc") return (b.priceHigh ?? 0) - (a.priceHigh ?? 0) || a.order - b.order;
      return a.order - b.order;
    });

  const total = parsed.length;
  const doneCount = built.size;
  const pct = total ? doneCount / total : 0;

  return (
    <div
      className="mb-12 border border-amber-400/40 bg-gradient-to-br from-[#150c05] via-[#0d0810] to-[#100a05] p-5 md:p-7 shadow-[0_0_28px_-10px_rgba(176,112,255,0.35)]"
      data-testid="dd-picker"
    >
      {/* Header: progress ring + summary */}
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-7 mb-6">
        <ProgressRing pct={pct} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-amber-300 text-[10px] tracking-[0.3em] uppercase mb-2">
            <Sparkles size={11} strokeWidth={2.5} />
            Personal Product-Launch Calendar
          </div>
          <div className="font-display text-2xl md:text-3xl font-black text-zinc-100 leading-tight mb-1">
            {doneCount} of {total} built.
            {doneCount === 0 && (
              <span className="text-zinc-500 font-normal text-base md:text-lg ml-2">
                Pick one that fits this week&apos;s budget × build time.
              </span>
            )}
            {doneCount > 0 && doneCount < total && (
              <span className="text-amber-300/90 font-normal text-base md:text-lg ml-2">
                Momentum stacks — keep the streak.
              </span>
            )}
            {doneCount === total && (
              <span className="text-amber-300 font-normal text-base md:text-lg ml-2">
                Portfolio complete. Now scale the best 3.
              </span>
            )}
          </div>
          <div className="text-zinc-500 text-xs md:text-sm">
            Progress saved on this device. Tick items as you build & ship them.
          </div>
        </div>
      </div>

      {/* Controls: sort + filter + toggle-hide-built */}
      <div className="flex flex-wrap items-center gap-3 mb-5 pb-5 border-b border-amber-500/15">
        <label className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-zinc-400">
          <ArrowUpDown size={11} className="text-amber-400/80" />
          Sort
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-black border border-amber-400/30 text-zinc-100 text-xs tracking-normal font-sans px-2 py-1 focus:border-amber-400 focus:outline-none"
            data-testid="dd-sort"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-zinc-400">
          <Filter size={11} className="text-amber-400/80" />
          Budget
          <select
            value={band}
            onChange={(e) => setBand(e.target.value)}
            className="bg-black border border-amber-400/30 text-zinc-100 text-xs tracking-normal font-sans px-2 py-1 focus:border-amber-400 focus:outline-none"
            data-testid="dd-filter"
          >
            {PRICE_BANDS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-zinc-400 ml-auto cursor-pointer">
          <input
            type="checkbox"
            checked={showBuilt}
            onChange={(e) => setShowBuilt(e.target.checked)}
            className="accent-amber-400"
            data-testid="dd-show-built"
          />
          Show built
        </label>
      </div>

      {/* Product rows */}
      <div className="divide-y divide-zinc-900" data-testid="dd-picker-list">
        {filtered.length === 0 && (
          <div className="py-8 text-center text-zinc-500 text-sm" data-testid="dd-picker-empty">
            No products match this filter.
          </div>
        )}
        {filtered.map((p) => {
          const isBuilt = built.has(p.idx);
          const buildLabel = p.buildLow
            ? p.buildLow === p.buildHigh
              ? `${p.buildLow}d`
              : `${p.buildLow}–${p.buildHigh}d`
            : "—";
          const priceLabel = p.priceLow ? `£${p.priceLow}–£${p.priceHigh}` : "—";
          return (
            <div
              key={p.idx}
              className={`grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-3 py-3 px-1 transition-colors ${
                isBuilt ? "opacity-60" : "hover:bg-amber-500/[0.03]"
              }`}
              data-testid={`dd-row-${p.order}`}
            >
              <button
                onClick={() => toggle(p.idx)}
                className={`w-7 h-7 inline-flex items-center justify-center border transition-all ${
                  isBuilt
                    ? "border-amber-400 bg-amber-400 text-black"
                    : "border-zinc-700 hover:border-amber-400 text-transparent"
                }`}
                aria-label={isBuilt ? "Mark as not built" : "Mark as built"}
                data-testid={`dd-toggle-${p.order}`}
              >
                <Check size={14} strokeWidth={3} />
              </button>
              <button
                onClick={() => onJump && onJump(p.idx)}
                className="text-left min-w-0"
                data-testid={`dd-jump-${p.order}`}
              >
                <div className="text-zinc-600 text-[10px] tracking-[0.25em] uppercase font-mono">
                  {String(p.order).padStart(2, "0")}
                </div>
                <div className={`font-display text-base md:text-lg font-semibold truncate ${isBuilt ? "line-through text-zinc-500" : "text-zinc-100 hover:text-amber-300"} transition-colors`}>
                  {p.name}
                </div>
              </button>
              <div className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-purple-300/80 font-mono tracking-wider">
                <Clock size={11} />
                {buildLabel}
              </div>
              <div className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-amber-300/90 font-mono tracking-wider min-w-[68px] justify-end">
                <Coins size={11} />
                {priceLabel}
              </div>
              <button
                onClick={() => onJump && onJump(p.idx)}
                className={`text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1.5 border transition-all ${
                  isBuilt
                    ? "border-zinc-800 text-zinc-600"
                    : "border-amber-400/40 hover:border-amber-400 text-amber-300 hover:bg-amber-400/[0.08]"
                }`}
                data-testid={`dd-open-${p.order}`}
              >
                {isBuilt ? "Review" : "Open"}
              </button>
            </div>
          );
        })}
      </div>

      {doneCount > 0 && (
        <div className="mt-6 pt-5 border-t border-amber-500/15 flex items-center justify-between">
          <div className="text-zinc-500 text-xs">
            Locally stored. Clear anytime.
          </div>
          <button
            onClick={() => {
              if (confirm("Clear all 'built' ticks?")) setBuilt(new Set());
            }}
            className="text-xs tracking-[0.25em] uppercase text-zinc-600 hover:text-red-400 transition-colors"
            data-testid="dd-reset"
          >
            Reset progress
          </button>
        </div>
      )}
    </div>
  );
}
